import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { useNotifications } from '../hooks/useNotifications';
import { useMarkRead } from '../hooks/useMarkRead';
import { useNotificationNavigation } from '../hooks/useNotificationNavigation';
import NotificationItemComponent from './NotificationItem';
import { NOTIFICATION_DROPDOWN_LIMIT } from '../constants';

interface Props {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

export default function NotificationDropdown({ anchorEl, open, onClose }: Props) {
  const { data: notifications, unreadCount } = useNotifications();
  const markRead = useMarkRead();
  const navigateToTarget = useNotificationNavigation();

  const items = notifications?.slice(0, NOTIFICATION_DROPDOWN_LIMIT) ?? [];

  function handleItemClick(notification: (typeof items)[0]) {
    if (!notification.isRead) {
      markRead.mutate({ ids: [notification.id] });
    }
    if (notification.target) {
      navigateToTarget(notification.target);
    }
    onClose();
  }

  function handleMarkAllRead() {
    markRead.mutate({ all: true });
  }

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 360, maxHeight: 480 } } }}
      data-testid="notification-dropdown"
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 1.5,
          pb: 0.5,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Notifications {unreadCount > 0 && `(${unreadCount})`}
        </Typography>
        {unreadCount > 0 && (
          <Button
            size="small"
            startIcon={<DoneAllIcon />}
            onClick={handleMarkAllRead}
            disabled={markRead.isPending}
            sx={{ textTransform: 'none' }}
          >
            Mark all read
          </Button>
        )}
      </Box>
      <Divider />
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
          No notifications
        </Typography>
      ) : (
        <List disablePadding sx={{ overflowY: 'auto' }}>
          {items.map((notif) => (
            <NotificationItemComponent
              key={notif.id}
              notification={notif}
              onClick={() => handleItemClick(notif)}
            />
          ))}
        </List>
      )}
    </Popover>
  );
}
